import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../hooks/useStore";
import {
  getCompanyById,
  updateCompanyStatus,
  deleteCompany,
  getCompanyUsers,
} from "../../store/slices/superAdminCompanySlice";
import {
  Building2,
  Mail,
  Phone,
  MapPin,
  Calendar,
  Trash2,
  Power,
  ArrowLeft,
  Edit,
} from "lucide-react";
import Button from "../../components/UI/Button";
import { Modal } from "../../components/UI/Modal";
import CompanyStatusBadge from "../../components/superadmin/companies/CompanyStatusBadge";
import CompanyTypeBadge from "../../components/superadmin/companies/CompanyTypeBadge";
import CompanyModulesCard from "../../components/superadmin/companies/CompanyModulesCard";
import { toast } from "react-toastify";
import { Input, FormField } from "../../components/UI/FormField";
import { ROOT_DOMAIN } from "../../utils/getRootDomain";

const CompanyDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { currentCompany, loading } = useAppSelector(
    (state) => state.superAdminCompanies,
  );

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [confirmSubdomain, setConfirmSubdomain] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);
  const [hasFetched, setHasFetched] = useState(false); // ← tracks whether initial fetch has completed

  useEffect(() => {
    if (id) {
      setHasFetched(false); // reset on id change (e.g. navigating between companies)
      Promise.all([
        dispatch(getCompanyById(id)),
        dispatch(getCompanyUsers({ id, params: { page: 1, limit: 10 } })),
      ]).finally(() => setHasFetched(true)); // mark fetch as done regardless of success/failure
    }
  }, [dispatch, id]);

  const handleStatusToggle = async () => {
    if (!currentCompany || !id) return;

    const newStatus =
      currentCompany.status === "active" ? "inactive" : "active";

    try {
      await dispatch(updateCompanyStatus({ id, status: newStatus })).unwrap();
      toast.success(
        `Company ${newStatus === "active" ? "activated" : "deactivated"} successfully`,
      );
    } catch (err: any) {
      toast.error(err || "Failed to update company status");
    }
  };

  const handleDelete = async () => {
    if (!currentCompany || !id) return;

    if (confirmSubdomain !== currentCompany.subdomain) {
      toast.error("Subdomain confirmation does not match");
      return;
    }

    setIsDeleting(true);
    try {
      await dispatch(deleteCompany({ id, confirmSubdomain })).unwrap();
      toast.success("Company deleted successfully");
      navigate("/companies");
    } catch (err: any) {
      toast.error(err || "Failed to delete company");
      setIsDeleting(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric",
    });
  };

  // Show spinner while fetch is in-flight OR before it has even started.
  // This prevents the "not found" flash on hard refresh when loading
  // is still false but currentCompany is also null (pre-fetch state).
  if (loading || !hasFetched) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  // Only shown after the fetch has fully completed and company is genuinely absent
  if (!currentCompany) {
    return (
      <div className="p-6">
        <div className="text-center py-12">
          <Building2 className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">
            Company not found
          </h3>
          <div className="mt-6">
            <Link to="/companies">
              <Button variant="primary">Back to Companies</Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Link to="/companies">
            <Button variant="outline" size="sm">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Button>
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {currentCompany.name}
            </h1>
            <p className="text-sm text-gray-500">
              {currentCompany.subdomain}.{ROOT_DOMAIN}
            </p>
          </div>
        </div>

        <div className="flex items-center space-x-3">
          <Button
            variant="outline"
            onClick={handleStatusToggle}
            disabled={loading}
          >
            <Power className="h-4 w-4 mr-2" />
            {currentCompany.status === "active" ? "Deactivate" : "Activate"}
          </Button>
          <Link to={`/companies/${id}/edit`}>
            <Button variant="outline">
              <Edit className="h-4 w-4 mr-2" />
              Edit
            </Button>
          </Link>
          <Button
            variant="outline"
            onClick={() => setShowDeleteModal(true)}
            className="text-red-600 hover:text-red-700"
          >
            <Trash2 className="h-4 w-4 mr-2" />
            Delete
          </Button>
        </div>
      </div>

      {/* Company Info Cards */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Company Details */}
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white shadow-sm rounded-lg border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-medium text-gray-900">
                Company Information
              </h2>
              <div className="flex items-center gap-2">
                <CompanyTypeBadge companyType={currentCompany.companyType} />
                <CompanyStatusBadge status={currentCompany.status} />
              </div>
            </div>

            <div className="space-y-4">
              <div className="flex items-start space-x-3">
                <Building2 className="h-5 w-5 text-gray-400 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-gray-500">
                    Company Name
                  </p>
                  <p className="text-sm text-gray-900">{currentCompany.name}</p>
                </div>
              </div>

              <div className="flex items-start space-x-3">
                <Mail className="h-5 w-5 text-gray-400 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-gray-500">Subdomain</p>
                  <p className="text-sm text-gray-900">
                    {currentCompany.subdomain}.{ROOT_DOMAIN}
                  </p>
                </div>
              </div>

              {currentCompany.phone && (
                <div className="flex items-start space-x-3">
                  <Phone className="h-5 w-5 text-gray-400 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-gray-500">Phone</p>
                    <p className="text-sm text-gray-900">
                      {currentCompany.phone}
                    </p>
                  </div>
                </div>
              )}

              {currentCompany.address && (
                <div className="flex items-start space-x-3">
                  <MapPin className="h-5 w-5 text-gray-400 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-gray-500">Address</p>
                    <p className="text-sm text-gray-900">
                      {[
                        currentCompany.address.street,
                        currentCompany.address.city,
                        currentCompany.address.state,
                        currentCompany.address.postalcode,
                      ]
                        .filter(Boolean)
                        .join(", ")}
                    </p>
                  </div>
                </div>
              )}

              {currentCompany.registrationOrLicenseNumber && (
                <div className="flex items-start space-x-3">
                  <Building2 className="h-5 w-5 text-gray-400 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-gray-500">
                      Registration Number
                    </p>
                    <p className="text-sm text-gray-900">
                      {currentCompany.registrationOrLicenseNumber}
                    </p>
                  </div>
                </div>
              )}

              <div className="flex items-start space-x-3">
                <Calendar className="h-5 w-5 text-gray-400 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-gray-500">Created</p>
                  <p className="text-sm text-gray-900">
                    {formatDate(currentCompany.createdAt)}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Recent Users */}
          {currentCompany.recentUsers &&
            currentCompany.recentUsers.length > 0 && (
              <div className="bg-white shadow-sm rounded-lg border border-gray-200">
                <div className="px-6 py-4 border-b border-gray-200">
                  <div className="flex items-center justify-between">
                    <h2 className="text-lg font-medium text-gray-900">
                      Recent Users
                    </h2>
                    {/* <Link to={`/companies/${id}/users`}>
                      <Button variant="outline" size="sm">
                        View All
                      </Button>
                    </Link> */}
                  </div>
                </div>
                <div className="divide-y divide-gray-200">
                  {currentCompany.recentUsers.map((user) => (
                    <div key={user._id} className="px-6 py-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-gray-900">
                            {user.firstName} {user.lastName}
                          </p>
                          <p className="text-sm text-gray-500">{user.email}</p>
                        </div>
                        <span
                          className={`px-2 py-1 text-xs font-medium rounded-full ${
                            user.status === "active"
                              ? "bg-green-100 text-green-800"
                              : user.status === "pending"
                                ? "bg-yellow-100 text-yellow-800"
                                : "bg-red-100 text-red-800"
                          }`}
                        >
                          {user.status}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
        </div>

        {/* Statistics Sidebar */}
        <div className="space-y-6">
          <div className="bg-white shadow-sm rounded-lg border border-gray-200 p-6">
            <h2 className="text-lg font-medium text-gray-900 mb-4">
              Statistics
            </h2>
            <div className="space-y-4">
              <div>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm text-gray-500">Total Users</span>
                  <span className="text-2xl font-bold text-gray-900">
                    {currentCompany.stats?.totalUsers || 0}
                  </span>
                </div>
              </div>
              <div>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm text-gray-500">Active Users</span>
                  <span className="text-2xl font-bold text-green-600">
                    {currentCompany.stats?.activeUsers || 0}
                  </span>
                </div>
              </div>
              <div>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm text-gray-500">Admin Users</span>
                  <span className="text-2xl font-bold text-blue-600">
                    {currentCompany.stats?.adminUsers || 0}
                  </span>
                </div>
              </div>
              {currentCompany.stats?.pendingUsers !== undefined && (
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm text-gray-500">Pending</span>
                    <span className="text-2xl font-bold text-yellow-600">
                      {currentCompany.stats.pendingUsers}
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Module management. Full width rather than in the sidebar column: it is
          a grid of every product surface, and squeezing it into a third of the
          page would wrap every label. */}
      <CompanyModulesCard
        companyId={currentCompany._id}
        enabledModules={currentCompany.enabledModules}
        updatedAt={currentCompany.updatedAt}
      />

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setConfirmSubdomain("");
        }}
        title="Delete Company"
        size="md"
      >
        <div className="space-y-4 p-4 bg-white">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-sm text-red-800">
              <strong>Warning:</strong> This action cannot be undone. This will
              permanently delete the company and all associated data.
            </p>
          </div>

          <FormField
            label={`Type "${currentCompany.subdomain}" to confirm`}
            required
          >
            <Input
              value={confirmSubdomain}
              onChange={(e) => setConfirmSubdomain(e.target.value)}
              placeholder={currentCompany.subdomain}
            />
          </FormField>

          <div className="flex space-x-3">
            <Button
              variant="outline"
              onClick={() => {
                setShowDeleteModal(false);
                setConfirmSubdomain("");
              }}
              className="flex-1"
              disabled={isDeleting}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleDelete}
              className="flex-1 bg-red-600 hover:bg-red-700"
              disabled={
                isDeleting || confirmSubdomain !== currentCompany.subdomain
              }
            >
              {isDeleting ? "Deleting..." : "Delete Company"}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default CompanyDetailPage;
